import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { routeService } from '../../../../application/RouteService';
import { Route } from '../../../../domain/route/Route';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseDuplicateRouteProps {
  label: string;
  execution_date: Date;
  route_id: string;
}

type UseDuplicateRouteResponseProps = ServiceResponse<
  ({ label, execution_date, route_id }: UseDuplicateRouteProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useDuplicateRoute = (): UseDuplicateRouteResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { routeDispatch } = useRouteContext();

  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Route, unknown, UseDuplicateRouteProps>(
    ({ label, execution_date, route_id }: UseDuplicateRouteProps): Promise<Route> => {
      return routeService.duplicateRoute({
        label,
        execution_date: execution_date,
        route_id,
      });
    },
    {
      onSuccess: (route) => {
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: route });
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, _variables: any, context: any) => {
        console.info(`Duplicate Route Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  const createRoute = ({ label, execution_date: executionDate, route_id }: UseDuplicateRouteProps) => {
    mutate({
      label,
      execution_date: executionDate,
      route_id,
    });
  };

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: createRoute,
  };
};
